/**
 * El movimiento del sitio: la animación como parte de la marca.
 *
 * Igual que los colores o la tipografía, se elige UNA vez para todo el sitio
 * (Marca › Animación) y cada elemento la hereda según lo que es —un título, un
 * botón, una tarjeta, una foto—. Se resuelve al dibujar y no se guarda en cada
 * elemento, así que cambiar el estilo cambia el sitio entero al momento. La
 * animación propia de un elemento (panel de animación) manda sobre ésta, como
 * un ajuste local sobre su token.
 *
 * Los nombres de las entradas y los hovers son los del editor
 * (`apps/editor/src/lib/animations.ts`): aquí sólo se eligen.
 */

export const SITE_MOTION_STYLES = ['none', 'subtle', 'smooth', 'elegant', 'slide', 'zoom', 'reveal', 'cinematic', 'dynamic', 'flip'] as const
export type SiteMotionStyle = (typeof SITE_MOTION_STYLES)[number]

/** Lo que hereda cada tipo de elemento. */
export const MOTION_ROLES = ['heading', 'subheading', 'text', 'button', 'card', 'image'] as const
export type MotionRole = (typeof MOTION_ROLES)[number]

export const MOTION_SPEEDS = ['slow', 'normal', 'fast'] as const
export type MotionSpeed = (typeof MOTION_SPEEDS)[number]

/** Ajustes del efecto hover, compartidos con las animaciones de elemento. */
export type MotionHoverSettings = {
  scale?: number
  lift?: number
  color?: string
  blur?: number
  opacity?: number
  tilt?: number
  outline?: number
}

export function normalizeMotionHoverSettings(input: unknown): MotionHoverSettings | undefined {
  if (!input || typeof input !== 'object') return undefined
  const raw = input as Record<string, unknown>
  const settings: MotionHoverSettings = {}
  const ranges = { scale: [85, 115], lift: [0, 24], blur: [0, 80], opacity: [0, 100], tilt: [-8, 8], outline: [1, 6] } as const
  for (const key of Object.keys(ranges) as Array<keyof typeof ranges>) {
    const value = raw[key]
    if (typeof value === 'number' && Number.isFinite(value)) {
      settings[key] = Math.round(Math.max(ranges[key][0], Math.min(ranges[key][1], value)))
    }
  }
  if (typeof raw.color === 'string' && /^(?:#(?:[\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})|rgba?\([\d\s.,%/]+\)|var\(--[\w-]+\))$/i.test(raw.color.trim())) settings.color = raw.color.trim()
  return Object.keys(settings).length ? settings : undefined
}

/** Lo que se puede cambiar de un tipo en «Personalizar». */
export type MotionRoleSetting = {
  /** La entrada; 'none' = ese tipo no se anima. */
  enter?: string | undefined
  /** El hover (sólo botones y tarjetas lo ofrecen); 'none' = sin hover. */
  hover?: string | undefined
  hoverSettings?: MotionHoverSettings | undefined
}

export type SiteMotion = {
  style: SiteMotionStyle
  /** Lo cambiado a mano sobre el estilo, por tipo. */
  roles?: Partial<Record<MotionRole, MotionRoleSetting>> | undefined
  /** 0 = lenta, 100 = muy rápida; los nombres anteriores siguen siendo válidos. */
  speed?: MotionSpeed | number | undefined
  /** % de la ventana desde abajo para empezar (ver `offset` en las animaciones). */
  offset?: number | undefined
  /** Cada vez que aparece, en vez de sólo la primera. */
  repeat?: boolean | undefined
}

/** Lo que trae cada estilo para cada tipo. */
export type MotionRoleRecipe = {
  enter: string
  duration: number
  distance?: 'sm' | 'md' | 'lg'
  easing?: 'smooth' | 'expo' | 'spring' | 'in-out' | 'linear'
  /** Entre una tarjeta (o pieza de texto) y la siguiente, en ms. */
  stagger?: number
  hover?: string
  hoverSettings?: MotionHoverSettings
}

/**
 * Los cinco estilos. Pocos y distintos de verdad: quien no sabe de animación
 * elige por cómo se siente su marca, no por nombres de efectos.
 *
 * Dentro de un estilo, TODOS los elementos entran igual —la misma entrada, la
 * misma duración—: con una distinta para títulos, fotos y tarjetas el sitio no
 * se sentía uniforme (él, 2026-10-07). Lo único que cambia entre tipos es el
 * orden (ver `ROLE_DELAY` en el editor) y el escalonado de las tarjetas. El
 * hover se configura manualmente por tipo; los estilos no lo incluyen.
 */
function uniform(recipe: Omit<MotionRoleRecipe, 'hover' | 'hoverSettings' | 'stagger'>, extra: { stagger: number }): Record<MotionRole, MotionRoleRecipe> {
  return {
    heading: { ...recipe },
    subheading: { ...recipe },
    text: { ...recipe },
    button: { ...recipe },
    card: { ...recipe, stagger: extra.stagger },
    image: { ...recipe },
  }
}

/* «Normal» son 2 s en todos los estilos, lo mismo que una animación puesta a
   mano (él, 2026-10-07); «Lenta» y «Rápida» la escalan (\`MOTION_SPEED_FACTOR\`). */
export const MOTION_NORMAL_MS = 2000

export const MOTION_STYLE_RECIPES: Record<Exclude<SiteMotionStyle, 'none'>, Record<MotionRole, MotionRoleRecipe>> = {
  /* Discreta, profesional: un fundido corto. */
  subtle: uniform({ enter: 'fade', duration: MOTION_NORMAL_MS }, { stagger: 90 }),
  /* Moderna: todo sube un poco al aparecer. */
  smooth: uniform({ enter: 'fade-up', duration: MOTION_NORMAL_MS, distance: 'sm' }, { stagger: 120 }),
  /* De estudio o marca de lujo: más lenta, con un desenfoque suave. */
  elegant: uniform({ enter: 'blur-in', duration: MOTION_NORMAL_MS }, { stagger: 160 }),
  /* Llega desde un lado: para sitios con aire de presentación. */
  slide: uniform({ enter: 'fade-left', duration: MOTION_NORMAL_MS, distance: 'md' }, { stagger: 120 }),
  /* Crece un poco al aparecer: cercana y alegre. */
  zoom: uniform({ enter: 'zoom-in', duration: MOTION_NORMAL_MS }, { stagger: 110 }),
  /* Se descubre de abajo arriba, como un telón: editorial. */
  reveal: uniform({ enter: 'reveal-up', duration: MOTION_NORMAL_MS, easing: 'expo' }, { stagger: 140 }),
  /* Llega de cerca y se asienta despacio, como un plano de cine. */
  cinematic: uniform({ enter: 'zoom-out', duration: MOTION_NORMAL_MS, easing: 'expo' }, { stagger: 160 }),
  /* Con energía: rápida, con un pequeño rebote. */
  dynamic: uniform({ enter: 'fade-up', duration: MOTION_NORMAL_MS, distance: 'md', easing: 'spring' }, { stagger: 80 }),
  /* Gira un poco al entrar: juguetona, para marcas desenfadadas. */
  flip: uniform({ enter: 'flip-up', duration: MOTION_NORMAL_MS }, { stagger: 100 }),
}

/** La velocidad, como multiplicador de la duración. */
export const MOTION_SPEED_FACTOR: Record<MotionSpeed, number> = { slow: 1.35, normal: 1, fast: 0.1 }

/** Posición del slider, preservando la velocidad normal de los temas guardados. */
export function motionSpeedPosition(speed: SiteMotion['speed']): number {
  if (typeof speed === 'number' && Number.isFinite(speed)) return Math.max(0, Math.min(100, Math.round(speed)))
  return speed === 'slow' ? 0 : speed === 'fast' ? 100 : 28
}

export function motionSpeedFactor(speed: SiteMotion['speed']): number {
  if (typeof speed !== 'number') return MOTION_SPEED_FACTOR[speed ?? 'normal']
  const position = motionSpeedPosition(speed) / 100
  return MOTION_SPEED_FACTOR.slow + (MOTION_SPEED_FACTOR.fast - MOTION_SPEED_FACTOR.slow) * position
}

/** Lo que hereda un tipo, con lo cambiado a mano y la velocidad ya aplicados. `null` = no se anima. */
export function resolveMotionRole(motion: SiteMotion | null | undefined, role: MotionRole): MotionRoleRecipe | null {
  if (!motion || motion.style === 'none') return null
  const base = MOTION_STYLE_RECIPES[motion.style][role]
  const custom = motion.roles?.[role]
  const enter = custom?.enter ?? base.enter
  const selectedHover = custom?.hover ?? base.hover
  const hover = role === 'card' && selectedHover === 'dim' ? 'none' : selectedHover
  if (enter === 'none' && (!hover || hover === 'none')) return null
  const factor = motionSpeedFactor(motion.speed)
  const { hover: _baseHover, ...rest } = base
  return {
    ...rest,
    enter,
    duration: Math.round(base.duration * factor / 50) * 50,
    ...(hover && hover !== 'none' ? { hover } : {}),
    ...(custom?.hoverSettings ? { hoverSettings: custom.hoverSettings } : {}),
  }
}

/** El movimiento guardado en el tema, saneado; `undefined` = sin movimiento (proyectos de antes). */
export function normalizeSiteMotion(input: unknown): SiteMotion | undefined {
  if (!input || typeof input !== 'object') return undefined
  const raw = input as Record<string, unknown>
  const style = (SITE_MOTION_STYLES as readonly unknown[]).includes(raw.style) ? raw.style as SiteMotionStyle : undefined
  if (!style) return undefined
  const roles: Partial<Record<MotionRole, MotionRoleSetting>> = {}
  if (raw.roles && typeof raw.roles === 'object') {
    for (const role of MOTION_ROLES) {
      const value = (raw.roles as Record<string, unknown>)[role]
      if (!value || typeof value !== 'object') continue
      const { enter, hover, hoverSettings: rawHoverSettings } = value as Record<string, unknown>
      const hoverSettings = normalizeMotionHoverSettings(rawHoverSettings)
      const setting: MotionRoleSetting = {
        ...(typeof enter === 'string' && enter ? { enter } : {}),
        ...(typeof hover === 'string' && hover ? { hover } : {}),
        ...(hoverSettings ? { hoverSettings } : {}),
      }
      if (Object.keys(setting).length) roles[role] = setting
    }
  }
  const speed = typeof raw.speed === 'number' && Number.isFinite(raw.speed)
    ? motionSpeedPosition(raw.speed)
    : (MOTION_SPEEDS as readonly unknown[]).includes(raw.speed) ? raw.speed as MotionSpeed : undefined
  const offset = typeof raw.offset === 'number' && Number.isFinite(raw.offset) ? Math.max(0, Math.min(50, Math.round(raw.offset))) : undefined
  return {
    style,
    ...(Object.keys(roles).length ? { roles } : {}),
    ...(speed !== undefined && speed !== 'normal' ? { speed } : {}),
    ...(offset !== undefined ? { offset } : {}),
    ...(raw.repeat === true ? { repeat: true } : {}),
  }
}

/* Los estilos visuales (`designStyles.ts`) por cómo se mueven. */
const ELEGANT_DESIGNS = new Set(['editorial', 'luxury', 'studio', 'gallery', 'calm', 'heritage', 'nordic', 'evergreen', 'advisory', 'capital', 'counsel', 'estate'])
const DYNAMIC_DESIGNS = new Set(['brutalist', 'creator', 'voltage', 'pulse', 'coral', 'retail', 'marketplace'])

/**
 * El estilo de movimiento que va con un estilo visual del sitio, para la IA y
 * los proyectos nuevos: un estudio editorial, «Elegante»; una marca enérgica,
 * «Dinámica»; lo demás, «Suave».
 */
export function motionStyleForDesign(designStyleId: string | null | undefined): SiteMotionStyle {
  const id = (designStyleId ?? '').toLowerCase()
  if (ELEGANT_DESIGNS.has(id)) return 'elegant'
  if (DYNAMIC_DESIGNS.has(id)) return 'dynamic'
  return 'smooth'
}
